"use client";

import * as AvatarPrimitive from "@radix-ui/react-avatar";
import { cva, VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "../lib/utils";

const avatarVariants = cva("relative flex shrink-0 overflow-hidden", {
  variants: {
    variant: {
      default: "rounded-full",
      square: "rounded-lg",
    },
    size: {
      default: "h-10 w-10",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "default",
  },
});

/**
 * Radix UI (API reference): https://www.radix-ui.com/docs/primitives/components/avatar#api-reference
 *
 * shadcn/ui docs: https://ui.shadcn.com/docs/components/avatar
 */
const Avatar = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root> &
    VariantProps<typeof avatarVariants>
>(({ className, variant, size, ...props }, ref) => (
  <AvatarPrimitive.Root
    className={cn(avatarVariants({ variant, size, className }))}
    ref={ref}
    {...props}
  />
));
Avatar.displayName = AvatarPrimitive.Root.displayName;

const AvatarImage = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Image>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image> &
    React.HTMLAttributes<typeof AvatarPrimitive.Image>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Image
    className={cn("aspect-square h-full w-full", className)}
    ref={ref}
    {...props}
  />
));
AvatarImage.displayName = AvatarPrimitive.Image.displayName;

const AvatarFallback = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Fallback>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Fallback
    className={cn(
      "flex h-full w-full items-center justify-center bg-muted font-medium",
      className,
    )}
    ref={ref}
    {...props}
  />
));
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;

type AvatarFallbackProps = React.ComponentProps<typeof AvatarFallback>;
type AvatarImageProps = React.ComponentProps<typeof AvatarImage>;
type AvatarProps = React.ComponentProps<typeof Avatar>;

export {
  Avatar,
  AvatarFallback,
  AvatarImage,
  avatarVariants,
  type AvatarFallbackProps,
  type AvatarImageProps,
  type AvatarProps,
};
